import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import {
  CalendarCheck2,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  MapPin,
  MoonStar,
  RefreshCw,
  XCircle,
} from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Kpi } from '@/components/ui/kpi'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { VisitPhoto } from '@/features/cases/VisitPhoto'

import { navPermissionCheck } from '@/components/layout/navVisibility'
import { useMe } from '@/features/auth/api'
import { dateTime, relativeTime } from '@/lib/format'
import { useLabel } from '@/lib/labels'

import {
  useApprovalInbox,
  useApprovedPending,
  useDecideHeldVisit,
  useDecideTripRequest,
  useDecideWeeklyPlan,
  useHeldVisits,
  VISIT_REASONS,
  type HeldVisit,
  type InboxStop,
  type TripRequestRow,
  type VisitReason,
  type WeeklyPlanRow,
  type WeeklyStopsByDay,
} from './api'

type Tab = 'all' | 'trips' | 'plans'

export function ApprovalCenterPage() {
  const [tab, setTab] = useState<Tab>('all')
  const [agentFilter, setAgentFilter] = useState('')
  const inbox = useApprovalInbox(agentFilter.trim() || undefined)
  const approvedPending = useApprovedPending()
  const held = useHeldVisits()

  const tripCount = inbox.data?.tripRequests.length ?? 0
  const planCount = inbox.data?.weeklyPlans.length ?? 0
  const total = tripCount + planCount

  const oldest = useMemo(() => {
    const all: { createdAt: string }[] = [
      ...(inbox.data?.tripRequests ?? []),
      ...(inbox.data?.weeklyPlans ?? []),
    ]
    if (all.length === 0) return null
    return all.reduce((acc, x) => (x.createdAt < acc ? x.createdAt : acc), all[0]!.createdAt)
  }, [inbox.data])

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold">
            <ClipboardCheck className="h-5 w-5" />
            Approval Center
          </h1>
          <p className="text-sm text-muted-foreground">
            Pending field-visit approvals pinned to you or for agents in your approval scope —
            oldest first.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => inbox.refetch()}
          disabled={inbox.isFetching}
        >
          <RefreshCw className={`mr-2 h-4 w-4 ${inbox.isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </header>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-5">
        <Kpi label="Pending approval" value={total} />
        <Kpi label="Ad-hoc visit requests" value={tripCount} />
        <Kpi label="Weekly plans" value={planCount} hint={oldest ? `oldest ${relativeTime(oldest)}` : '—'} />
        <Kpi
          label="Approved — awaiting start"
          value={approvedPending.data?.tripRequests.length ?? 0}
          hint="Agent hasn't tapped Start yet"
        />
        <Kpi label="Held visits" value={held.data?.length ?? 0} hint="Marked outside the contact window" />
      </div>

      <Card>
        <CardHeader className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <ChipGroup>
              <Chip active={tab ==='all'} onClick={() => setTab('all')}>All ({total})</Chip>
              <Chip active={tab ==='trips'} onClick={() => setTab('trips')}>
                Ad-hoc ({tripCount})
              </Chip>
              <Chip active={tab ==='plans'} onClick={() => setTab('plans')}>
                Plans ({planCount})
              </Chip>
            </ChipGroup>
            <Input
              placeholder="Filter by agent UUID"
              value={agentFilter}
              onChange={(e) => setAgentFilter(e.target.value)}
              className="w-72"
            />
          </div>
          {inbox.isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load inbox</AlertTitle>
              <AlertDescription>
                {isAxiosError(inbox.error) ? inbox.error.message : String(inbox.error)}
              </AlertDescription>
            </Alert>
          )}
        </CardHeader>

        <CardContent>
          {inbox.isLoading && <SkeletonRows rows={4} />}
          {!inbox.isLoading && total === 0 && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              Nothing pending. Approvals will appear here as your reportees submit them.
            </p>
          )}

          <ul className="space-y-3">
            {(tab === 'all' || tab === 'trips') &&
              (inbox.data?.tripRequests ?? []).map((r) => (
                <TripRequestItem key={r.tripRequestId} row={r} />
              ))}
            {(tab === 'all' || tab === 'plans') &&
              (inbox.data?.weeklyPlans ?? []).map((p) => (
                <WeeklyPlanItem key={p.weeklyPlanId} row={p} />
              ))}
          </ul>
        </CardContent>

        <CardFooter className="text-xs text-muted-foreground">
          {inbox.dataUpdatedAt > 0 && <>Last refreshed {relativeTime(new Date(inbox.dataUpdatedAt).toISOString())}</>}
        </CardFooter>
      </Card>

      <HeldVisitsCard />

      <ApprovedPendingCard />
    </div>
  )
}

/**
 * Visits an agent marked outside the contact window (or on a holiday). The
 * visit and its contact are already recorded; what the manager decides is
 * whether it counts toward the agent's visit stats.
 */
function HeldVisitsCard() {
  const held = useHeldVisits()
  const rows = held.data ?? []
  return (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <MoonStar className="h-4 w-4" />
          Held visits ({rows.length})
        </h2>
        <p className="text-xs text-muted-foreground">
          Visits marked outside the contact window. The visit is on record either way; approving
          counts it toward the agent&apos;s visits, rejecting doesn&apos;t.
        </p>
      </CardHeader>
      <CardContent>
        {held.isLoading && <SkeletonRows rows={3} />}
        {held.isError && (
          <Alert variant="destructive">
            <AlertTitle>Could not load held visits</AlertTitle>
            <AlertDescription>{decisionErrorMessage(held.error)}</AlertDescription>
          </Alert>
        )}
        {!held.isLoading && !held.isError && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No held visits. Visits your agents mark outside the contact window appear here.
          </p>
        )}
        <ul className="space-y-3">
          {rows.map((v) => (
            <HeldVisitItem key={v.visitId} visit={v} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function HeldVisitItem({ visit }: { visit: HeldVisit }) {
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const decide = useDecideHeldVisit()
  const label = useLabel()
  const rights = useDecisionRights()

  async function act(decision: 'approve' | 'reject') {
    try {
      const trimmed = note.trim()
      await decide.mutateAsync(trimmed ? { visitId: visit.visitId, decision, note: trimmed } : { visitId: visit.visitId, decision })
      setOpen(false)
      setNote('')
    } catch {
      /* surfaced via decide.isError below */
    }
  }

  return (
    <li className="rounded-md border bg-card p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {visit.agentEmpId} · {visit.agentFullName} — {visit.customerName ?? visit.loanAccountNo}
          </span>
          <Badge tone="navy">{label('visitReason', visit.visitReason)}</Badge>
          <Badge tone="slate">{label('visitOutcome', visit.outcome)}</Badge>
          {visit.mockLocationDetected && <Badge tone="negative">Mock location</Badge>}
          <PinBadge mappedManagerUserId={visit.mappedManagerUserId} viewerId={rights.userId} />
        </div>
        <span className="text-xs text-muted-foreground">visited {dateTime(visit.occurredAt)}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Detail label="Loan">{visit.loanAccountNo}</Detail>
            <Detail label="Why held">{visit.heldReason ?? '—'}</Detail>
            <Detail label="Location">
              {visit.geoLat != null && visit.geoLng != null ? (
                <a
                  className="underline"
                  href={`https://www.google.com/maps?q=${visit.geoLat},${visit.geoLng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {Number(visit.geoLat).toFixed(5)}, {Number(visit.geoLng).toFixed(5)}
                  {visit.geoAccuracyM != null && ` (±${Math.round(Number(visit.geoAccuracyM))} m)`}
                </a>
              ) : (
                'Not captured'
              )}
            </Detail>
          </div>
          {visit.notes && <p className="text-sm">{visit.notes}</p>}
          {visit.hasPhoto ? (
            <VisitPhoto visitId={visit.visitId} open />
          ) : (
            <p className="text-xs text-muted-foreground">No photo was taken on this visit.</p>
          )}
          <Textarea
            placeholder="Optional note (required if rejecting)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={1024}
          />
          {decide.isError && (
            <Alert variant="destructive">
              <AlertDescription>{decisionErrorMessage(decide.error)}</AlertDescription>
            </Alert>
          )}
          {rights.canApproveTrips ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => act('approve')} disabled={decide.isPending}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => act('reject')}
                disabled={decide.isPending || !note.trim()}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <span className="text-xs text-muted-foreground">Reject requires a reason. {WHO_DECIDES}</span>
            </div>
          ) : (
            <NoDecisionRight permission="Visit approval" />
          )}
        </div>
      )}
    </li>
  )
}

/**
 * Closes the visibility gap that left a TL blind to a trip between
 * "I approved it" and "the agent finally tapped Start". Reuses the
 * same per-stop rendering as the inbox; no approve/reject buttons
 * since the agent owns this state — only visibility + a hint that
 * they can ping the agent if it's sitting too long.
 */
function ApprovedPendingCard() {
  const pending = useApprovedPending()
  const rows = pending.data?.tripRequests ?? []

  return (
    <Card>
      <CardHeader>
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <CalendarDays className="h-4 w-4" />
          Approved · awaiting start ({rows.length})
        </h2>
        <p className="text-xs text-muted-foreground">
          Approved visit requests where the agent hasn't tapped Start yet.
          Ping them if it's been a while and the visit window is closing.
        </p>
      </CardHeader>
      <CardContent>
        {pending.isLoading && <SkeletonRows rows={3} />}
        {!pending.isLoading && rows.length === 0 && (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nothing waiting. Approved visits appear here only while the agent
            hasn't started them yet — they move into Visit review on start.
          </p>
        )}
        <ul className="space-y-3">
          {rows.map((r) => (
            <ApprovedPendingItem key={r.tripRequestId} row={r} />
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}

function ApprovedPendingItem({ row }: { row: TripRequestRow }) {
  const [open, setOpen] = useState(false)
  return (
    <li className="rounded-md border bg-card p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {row.agentEmpId} · {row.agentFullName} — {row.scheduledDate} {row.scheduledStartTime}
          </span>
          <Badge tone="slate">{row.stopCount} stop{row.stopCount === 1 ? '' : 's'}</Badge>
          {row.weeklyPlanId && <Badge tone="info">child of plan</Badge>}
        </div>
        <span className="text-xs text-muted-foreground">
          approved · scheduled {row.scheduledDate}
        </span>
      </button>
      {open && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Detail label="Agent">{row.agentEmpId} · {row.agentFullName}</Detail>
            <Detail label="Visit request">{shortId(row.tripRequestId)}</Detail>
            <Detail label="Scheduled">{row.scheduledDate} {row.scheduledStartTime}</Detail>
          </div>
          <StopsTable stops={row.stops} />
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Trip-request item — expand to approve/reject
// ---------------------------------------------------------------------------

function TripRequestItem({ row }: { row: TripRequestRow }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const decide = useDecideTripRequest()
  const rights = useDecisionRights()

  async function act(decision: 'approve' | 'reject') {
    try {
      const trimmed = reason.trim()
      await decide.mutateAsync(
        trimmed
          ? { tripRequestId: row.tripRequestId, decision, reason: trimmed }
          : { tripRequestId: row.tripRequestId, decision },
      )
      setOpen(false)
      setReason('')
    } catch {
      /* error surfaced via decide.isError below */
    }
  }

  return (
    <li className="rounded-md border bg-card p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {row.agentEmpId} · {row.agentFullName} — {row.scheduledDate} {row.scheduledStartTime}
          </span>
          <Badge tone="slate">{row.stopCount} stop{row.stopCount === 1 ? '' : 's'}</Badge>
          {row.weeklyPlanId && <Badge tone="info">child of plan</Badge>}
          <PinBadge mappedManagerUserId={row.mappedManagerUserId} viewerId={rights.userId} />
        </div>
        <span className="text-xs text-muted-foreground">submitted {relativeTime(row.createdAt)}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Detail label="Agent">{row.agentEmpId} · {row.agentFullName}</Detail>
            <Detail label="Visit request">{shortId(row.tripRequestId)}</Detail>
            <Detail label="Submitted">{dateTime(row.createdAt)}</Detail>
          </div>
          <StopsTable stops={row.stops} />
          <Textarea
            placeholder="Optional reason (required if rejecting)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1024}
          />
          {decide.isError && (
            <Alert variant="destructive">
              <AlertDescription>{decisionErrorMessage(decide.error)}</AlertDescription>
            </Alert>
          )}
          {rights.canApproveTrips ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                onClick={() => act('approve')}
                disabled={decide.isPending}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => act('reject')}
                disabled={decide.isPending || !reason.trim()}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject
              </Button>
              <span className="text-xs text-muted-foreground">Reject requires a reason. {WHO_DECIDES}</span>
            </div>
          ) : (
            <NoDecisionRight permission="Visit approval" />
          )}
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Weekly-plan item — expand to approve/reject
// ---------------------------------------------------------------------------

function WeeklyPlanItem({ row }: { row: WeeklyPlanRow }) {
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const decide = useDecideWeeklyPlan()
  const rights = useDecisionRights()

  async function act(decision: 'approve' | 'reject') {
    try {
      const trimmed = reason.trim()
      await decide.mutateAsync(
        trimmed
          ? { planId: row.weeklyPlanId, decision, reason: trimmed }
          : { planId: row.weeklyPlanId, decision },
      )
      setOpen(false)
      setReason('')
    } catch {
      /* surfaced below */
    }
  }

  return (
    <li className="rounded-md border bg-card p-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
      >
        <div className="flex flex-wrap items-center gap-2">
          <CalendarCheck2 className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">
            {row.agentEmpId} · {row.agentFullName} — week of {row.weekStartDate}
          </span>
          <Badge tone="slate">{row.stopCount} stop{row.stopCount === 1 ? '' : 's'}</Badge>
          <PinBadge mappedManagerUserId={row.mappedManagerUserId} viewerId={rights.userId} />
        </div>
        <span className="text-xs text-muted-foreground">submitted {relativeTime(row.createdAt)}</span>
      </button>

      {open && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Detail label="Agent">{row.agentEmpId} · {row.agentFullName}</Detail>
            <Detail label="Plan">{shortId(row.weeklyPlanId)}</Detail>
            <Detail label="Submitted">{dateTime(row.createdAt)}</Detail>
          </div>
          <WeeklyStopsTable stopsByDay={row.stopsByDay} />
          <p className="text-xs text-muted-foreground">
            Approving cascade-approves any pending child visit requests in this week.
          </p>
          {decide.data && decide.data.cascadedTripRequestCount > 0 && (
            <Alert>
              <AlertTitle>Plan approved</AlertTitle>
              <AlertDescription>
                Cascade-approved {decide.data.cascadedTripRequestCount} pending child visit
                request{decide.data.cascadedTripRequestCount === 1 ? '' : 's'}.
              </AlertDescription>
            </Alert>
          )}
          <Textarea
            placeholder="Optional reason (required if rejecting)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1024}
          />
          {decide.isError && (
            <Alert variant="destructive">
              <AlertDescription>{decisionErrorMessage(decide.error)}</AlertDescription>
            </Alert>
          )}
          {rights.canApprovePlans ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" onClick={() => act('approve')} disabled={decide.isPending}>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve plan
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => act('reject')}
                disabled={decide.isPending || !reason.trim()}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Reject plan
              </Button>
            </div>
          ) : (
            <NoDecisionRight permission="Weekly plan approval" />
          )}
        </div>
      )}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const WHO_DECIDES =
  'The mapped manager, or anyone whose approval scope covers the agent, can decide.'

/**
 * Which decisions the viewer may take. Cosmetic, like the nav: the API
 * enforces TRIP_REQUEST_APPROVE (trip requests, held visits) and
 * WEEKLY_PLAN_APPROVE (plans) itself, and checks the agent is in scope.
 */
function useDecisionRights() {
  const { data: me } = useMe()
  const can = navPermissionCheck(me)
  return {
    userId: me?.userId,
    canApproveTrips: can('TRIP_REQUEST_APPROVE'),
    canApprovePlans: can('WEEKLY_PLAN_APPROVE'),
  }
}

/**
 * An in-scope approver also sees items pinned to another manager (or, for
 * a held visit, to nobody); say so, so they know they're stepping in.
 */
function PinBadge({
  mappedManagerUserId,
  viewerId,
}: {
  mappedManagerUserId: string | null | undefined
  viewerId: string | undefined
}) {
  // undefined = an older API that doesn't send the field; show nothing.
  if (mappedManagerUserId === undefined || !viewerId) return null
  if (mappedManagerUserId === null) return <Badge tone="warning">No mapped manager</Badge>
  if (mappedManagerUserId === viewerId) return null
  return <Badge tone="slate">Mapped to another manager</Badge>
}

function NoDecisionRight({ permission }: { permission: string }) {
  return (
    <p className="text-xs text-muted-foreground">
      You can see this, but deciding needs the {permission} permission. Ask an admin to
      enable it in Roles &amp; permissions.
    </p>
  )
}

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="font-mono text-xs">{children}</div>
    </div>
  )
}

const VISIT_REASON_LABEL: Record<VisitReason, string> = Object.fromEntries(
  VISIT_REASONS.map((r) => [r.code, r.label]),
) as Record<VisitReason, string>

/** Per-stop table — what the agent actually plans to do. */
function StopsTable({ stops }: { stops: InboxStop[] }) {
  if (!stops || stops.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No stops planned — an ad-hoc visit request with no customers on it.
      </p>
    )
  }
  return (
    <div className="overflow-hidden rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-navy-50/40 text-left text-2xs uppercase tracking-wider text-navy-700">
          <tr>
            <th className="px-3 py-2 font-medium">#</th>
            <th className="px-3 py-2 font-medium">Loan / customer</th>
            <th className="px-3 py-2 font-medium">Visit reason</th>
            <th className="px-3 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {stops.map((s, i) => (
            <tr key={`${s.caseId}-${i}`} className="border-t">
              <td className="px-3 py-2 font-mono text-2xs text-muted-foreground">{i + 1}</td>
              <td className="px-3 py-2">
                <div className="font-mono text-xs">{s.loanAccountNo ?? shortId(s.caseId)}</div>
                {s.customerId && (
                  <div className="text-2xs text-muted-foreground">cust {shortId(s.customerId)}</div>
                )}
              </td>
              <td className="px-3 py-2">
                <Badge tone="navy">{VISIT_REASON_LABEL[s.visitReason] ?? s.visitReason}</Badge>
                {s.slotStart && s.slotEnd && (
                  <div className="mt-1 text-2xs text-muted-foreground">
                    {s.slotStart}–{s.slotEnd}
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{s.notes || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const WEEKDAYS: Array<{ key: 'MON'|'TUE'|'WED'|'THU'|'FRI'|'SAT'|'SUN'; label: string }> = [
  { key: 'MON', label: 'Mon' },
  { key: 'TUE', label: 'Tue' },
  { key: 'WED', label: 'Wed' },
  { key: 'THU', label: 'Thu' },
  { key: 'FRI', label: 'Fri' },
  { key: 'SAT', label: 'Sat' },
  { key: 'SUN', label: 'Sun' },
]

/** Weekly-plan view: one collapsible block per weekday that has any stops. */
function WeeklyStopsTable({ stopsByDay }: { stopsByDay: WeeklyStopsByDay }) {
  const daysWithStops = WEEKDAYS.filter((d) => (stopsByDay?.[d.key]?.length ?? 0) > 0)
  if (daysWithStops.length === 0) {
    return (
      <p className="text-xs italic text-muted-foreground">
        No stops planned in this week.
      </p>
    )
  }
  return (
    <div className="space-y-3">
      {daysWithStops.map((d) => (
        <div key={d.key}>
          <div className="mb-1 flex items-center gap-2">
            <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
              {d.label}
            </span>
            <span className="text-2xs text-muted-foreground">
              {stopsByDay[d.key]!.length} stop{stopsByDay[d.key]!.length === 1 ? '' : 's'}
            </span>
          </div>
          <StopsTable stops={stopsByDay[d.key]!} />
        </div>
      ))}
    </div>
  )
}

function shortId(uuid: string) {
  return uuid.slice(0, 8) + '…'
}

function decisionErrorMessage(err: unknown): string {
  if (isAxiosError(err)) {
    const status = err.response?.status
    const code = (err.response?.data as { code?: string } | undefined)?.code
    const msg = (err.response?.data as { message?: string } | undefined)?.message
    if (status === 403 || code === 'mapped_manager_only' || code === 'not_your_held_visit')
      return 'This agent is outside your approval scope, and it isn’t pinned to you — the reporting line may have moved.'
    if (status === 409) return msg ?? 'This item is no longer pending.'
    if (status === 400 && code === 'bad_request') return msg ?? 'Bad request.'
    return msg ?? err.message
  }
  return String(err)
}
